<script lang="ts">
  import { onMount, tick, createEventDispatcher } from 'svelte';
  import { toast } from '../js/toast.js';
  import { api } from '../js/api.js';
  import { auth } from '../js/auth.js';
  import { spinner } from '../js/spinner.js';
  import { loadStripeScript } from '../js/stripe-loader.js';
  import Modal from './Modal.svelte';
  import { formatMoney, APP_CURRENCY } from '../../js/currency.js';
  import { isPlanDowngrade } from '../../js/planTier.js';

  export let showModal = false;
  export let plan: any = null;
  export let currentPlan: any = null;
  export let isOnTrial: boolean = false;
  export let billingInterval: 'monthly' | 'annual' = 'monthly';
  export let currencyCode: string = APP_CURRENCY;

  const dispatch = createEventDispatcher();

  $: isDowngrade = plan ? isPlanDowngrade(plan, currentPlan) : false;
  // During trial or downgrade, no CC needed
  $: needsPayment = !isOnTrial && !isDowngrade;

  let stripe: any;
  let elements: any;
  let cardNumber: any;
  let cardExpiry: any;
  let cardCvc: any;
  let cardholderName = '';
  let isProcessing = false;
  let modalWidth = 520;
  let savedCards: any[] = [];
  let selectedCardId: string | null = null;
  let useNewCard = false;
  let isLoadingCards = false;
  let cardsLoadedForPlan: string | null = null;

  const stripePublishableKey = typeof import.meta.env.VITE_STRIPE_KEY === 'string' ? (import.meta.env.VITE_STRIPE_KEY || '').trim() : '';

  // Load Stripe only when modal is shown and payment is needed
  $: if (showModal && needsPayment && stripePublishableKey && !stripe) {
    loadStripeScript().then((StripeFn) => {
      if (StripeFn && !stripe) {
        stripe = StripeFn(stripePublishableKey);
        elements = stripe.elements();
      }
    });
  }

  $: if (showModal && needsPayment && plan && plan.uuid !== cardsLoadedForPlan) {
    cardsLoadedForPlan = plan.uuid;
    loadSavedCards();
  }

  // Reset when modal closes
  $: if (!showModal) {
    cardsLoadedForPlan = null;
  }

  async function loadSavedCards() {
    if (isLoadingCards) return;
    isLoadingCards = true;
    try {
      const response = await api.getUserCards(($auth as any).user.uuid) as any;
      if (response && response.success) {
        savedCards = response.data || [];
        if (savedCards.length > 0) {
          selectedCardId = savedCards[0].id;
          useNewCard = false;
        } else {
          useNewCard = true;
        }
      } else {
        savedCards = [];
        useNewCard = true;
      }
    } catch (error) {
      console.error('Error loading cards:', error);
      savedCards = [];
      useNewCard = true;
    } finally {
      isLoadingCards = false;
      if (useNewCard) {
        tick().then(() => setupCardElements());
      }
    }
  }

  function handleUseNewCard() {
    useNewCard = true;
    selectedCardId = null;
    tick().then(() => setupCardElements());
  }

  function setupCardElements() {
    if (cardNumber) { cardNumber.destroy(); cardNumber = null; }
    if (cardExpiry) { cardExpiry.destroy(); cardExpiry = null; }
    if (cardCvc) { cardCvc.destroy(); cardCvc = null; }

    const style = {
      base: {
        color: '#32325d',
        fontFamily: '"Poppins", "Helvetica Neue", Helvetica, sans-serif',
        fontSmoothing: 'antialiased',
        fontSize: '16px',
        '::placeholder': { color: '#aab7c4' },
      },
      invalid: { color: '#fa755a', iconColor: '#fa755a' },
    };

    if (!stripe || !elements) return;

    const numberEl = document.getElementById('subscribe-card-number');
    const expiryEl = document.getElementById('subscribe-card-expiry');
    const cvcEl = document.getElementById('subscribe-card-cvc');

    if (numberEl && expiryEl && cvcEl) {
      cardNumber = elements.create('cardNumber', { style });
      cardNumber.mount('#subscribe-card-number');
      cardExpiry = elements.create('cardExpiry', { style });
      cardExpiry.mount('#subscribe-card-expiry');
      cardCvc = elements.create('cardCvc', { style });
      cardCvc.mount('#subscribe-card-cvc');
    }
  }

  function resetForm() {
    cardholderName = '';
    selectedCardId = null;
    useNewCard = savedCards.length === 0;
  }

  function handleClose() {
    resetForm();
    showModal = false;
  }

  function formatPrice(price: number): string {
    return formatMoney(Number(price) || 0, currencyCode);
  }

  function getCardBrandIcon(brand: string): string {
    const brandIcons: Record<string, string> = {
      'visa': 'cc visa',
      'mastercard': 'cc mastercard',
      'amex': 'cc amex',
      'discover': 'cc discover',
      'diners': 'cc diners club',
      'jcb': 'cc jcb',
    };
    return brandIcons[brand?.toLowerCase()] || 'credit card';
  }

  function capitalizeFirst(str: string): string {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  function getDisplayPrice(): string {
    if (!plan) return '';
    if (plan.included_briefs === 0) return formatPrice(plan.price);
    if (billingInterval === 'annual' && plan.annual_price) {
      return formatPrice(Math.round(parseFloat(plan.annual_price) / 12));
    }
    return formatPrice(plan.price);
  }

  function getPriceSuffix(): string {
    if (!plan) return '';
    return plan.included_briefs === 0 ? '/brief' : '/month';
  }

  async function handleSubmit() {
    if (!plan) return;

    let paymentMethodId: string | null = null;

    if (needsPayment) {
      if (useNewCard) {
        if (!stripe) {
          toast.showNotification(false, 'Payment is not configured. Please set VITE_STRIPE_KEY in your environment.');
          return;
        }
        if (!cardholderName.trim()) {
          toast.showNotification(false, 'Please enter the cardholder name');
          return;
        }

        isProcessing = true;

        try {
          const { error, paymentMethod } = await stripe.createPaymentMethod({
            type: 'card',
            card: cardNumber,
            billing_details: { name: cardholderName },
          });

          if (error) {
            toast.showNotification(false, error.message);
            isProcessing = false;
            return;
          }

          paymentMethodId = paymentMethod.id;
        } catch (err) {
          console.error('Error creating payment method:', err);
          toast.showNotification(false, 'Error processing card');
          isProcessing = false;
          return;
        }
      } else {
        if (!selectedCardId) {
          toast.showNotification(false, 'Please select a payment method');
          return;
        }
        paymentMethodId = selectedCardId;
      }
    }

    isProcessing = true;
    spinner.show();

    try {
      const teamUuid = ($auth as any).user?.active_team_uuid;
      let response: any;

      if (!teamUuid) {
        toast.showNotification(false, 'No active team found. Please contact support.');
        return;
      }
      response = await api.assignTeamPlan(
        teamUuid,
        plan.uuid,
        billingInterval,
        paymentMethodId ?? ''
      );

      if (response.success) {
        const message = isOnTrial
          ? response.message || `${plan.name} selected! Billing starts when your trial ends.`
          : isDowngrade
            ? response.message || `Downgrade to ${plan.name} scheduled!`
            : `Successfully subscribed to ${plan.name}!`;
        toast.showNotification(true, message);
        dispatch('subscribed', { plan, paymentMethodId, isDowngrade });
        handleClose();
      } else {
        toast.showNotification(false, response.message || 'Failed to subscribe to plan');
      }
    } catch (err) {
      console.error('Error subscribing to plan:', err);
      toast.showNotification(false, 'Error subscribing to plan');
    } finally {
      spinner.hide();
      isProcessing = false;
    }
  }

  function getHeaderText(): string {
    if (isOnTrial) return 'Select Your Plan';
    if (isDowngrade) return 'Schedule Plan Downgrade';
    return 'Subscribe to Plan';
  }

  function getButtonText(): string {
    if (isProcessing) return 'Processing...';
    if (isOnTrial) return 'Select Plan';
    if (isDowngrade) return 'Schedule Downgrade';
    return 'Subscribe Now';
  }
</script>

<Modal bind:showModal bind:modalWidth>
  <div slot="header">{getHeaderText()}</div>
  <div slot="body">
    {#if plan}
      <!-- Plan Summary -->
      <div class="plan-summary" class:downgrade={isDowngrade} class:trial={isOnTrial}>
        <div class="plan-summary-header">
          <div class="plan-icon" class:downgrade={isDowngrade} class:trial={isOnTrial}>
            <i class="{isOnTrial ? 'star' : isDowngrade ? 'arrow down' : 'credit card outline'} icon"></i>
          </div>
          <div class="plan-info">
            <h3 class="plan-name">{plan.name}</h3>
            <div class="plan-price">
              <span class="price-amount">{getDisplayPrice()}</span>
              <span class="price-period">{getPriceSuffix()}</span>
            </div>
          </div>
        </div>

        <div class="plan-features-summary">
          {#if plan.included_briefs > 0}
            <div class="feature-item">
              <i class="check circle icon"></i>
              <span>{plan.included_briefs} briefs included per month</span>
            </div>
          {/if}
          {#if plan.extra_brief_price && plan.included_briefs > 0}
            <div class="feature-item">
              <i class="plus circle icon"></i>
              <span>Extra briefs at {formatPrice(plan.extra_brief_price)} each</span>
            </div>
          {/if}
          {#if plan.included_briefs === 0}
            <div class="feature-item">
              <i class="check circle icon"></i>
              <span>Pay per brief — {formatPrice(plan.price)} each</span>
            </div>
          {/if}
        </div>

        <div class="billing-note" class:downgrade={isDowngrade} class:trial={isOnTrial}>
          {#if isOnTrial}
            <i class="shield icon"></i>
            <span>No credit card required. Your 14-day free trial continues with unlimited usage. Billing starts only after the trial ends.</span>
          {:else if isDowngrade}
            <i class="clock outline icon"></i>
            <span>This change will take effect on your next billing date. Your current plan will remain active until then.</span>
          {:else}
            <i class="calendar alternate outline icon"></i>
            <span>
              {#if billingInterval === 'annual' && plan.annual_price}
                You'll be charged {formatPrice(plan.annual_price)} annually
              {:else}
                You'll be charged {formatPrice(plan.price)} monthly
              {/if}
            </span>
          {/if}
        </div>
      </div>

      {#if needsPayment}
        <div class="divider"></div>

        <!-- Payment Method Selection -->
        <div class="payment-section">
          <h4 class="section-title">Payment Method</h4>

          {#if isLoadingCards}
            <div class="cards-loading">
              <i class="spinner loading icon"></i>
              <span>Loading payment methods...</span>
            </div>
          {:else}
            {#if savedCards.length > 0}
              <div class="payment-options">
                {#each savedCards as card}
                  <label class="payment-option" class:selected={!useNewCard && selectedCardId === card.id}>
                    <input
                      type="radio"
                      name="payment-method"
                      value={card.id}
                      checked={!useNewCard && selectedCardId === card.id}
                      on:change={() => { selectedCardId = card.id; useNewCard = false; }}
                    />
                    <div class="option-content">
                      <i class="{getCardBrandIcon(card.brand)} icon card-brand-icon"></i>
                      <span class="card-info">
                        {capitalizeFirst(card.brand)} •••• {card.last4}
                      </span>
                    </div>
                  </label>
                {/each}

                <label class="payment-option" class:selected={useNewCard}>
                  <input
                    type="radio"
                    name="payment-method"
                    value="new"
                    checked={useNewCard}
                    on:change={handleUseNewCard}
                  />
                  <div class="option-content">
                    <i class="plus icon"></i>
                    <span>Add new card</span>
                  </div>
                </label>
              </div>
            {/if}

            {#if useNewCard}
              <div class="new-card-form" class:has-saved-cards={savedCards.length > 0}>
                <div class="form-field">
                  <label for="subscribe-cardholder-name" class="field-label">Name on Card</label>
                  <input
                    id="subscribe-cardholder-name"
                    type="text"
                    class="field-input"
                    bind:value={cardholderName}
                    placeholder="John Smith"
                    required
                  />
                </div>
                <div class="form-field">
                  <span class="field-label" id="subscribe-card-number-label">Card Number</span>
                  <div id="subscribe-card-number" class="stripe-input" aria-labelledby="subscribe-card-number-label"></div>
                </div>
                <div class="form-row">
                  <div class="form-field half">
                    <span class="field-label" id="subscribe-card-expiry-label">Expiry Date</span>
                    <div id="subscribe-card-expiry" class="stripe-input" aria-labelledby="subscribe-card-expiry-label"></div>
                  </div>
                  <div class="form-field half">
                    <span class="field-label" id="subscribe-card-cvc-label">CVC</span>
                    <div id="subscribe-card-cvc" class="stripe-input" aria-labelledby="subscribe-card-cvc-label"></div>
                  </div>
                </div>
              </div>
            {/if}
          {/if}
        </div>
      {/if}
    {/if}
  </div>
  <div slot="footer">
    <div class="modal-actions">
      <button class="cancel-button" on:click|preventDefault={handleClose} disabled={isProcessing}>
        Cancel
      </button>
      <button
        class="subscribe-button"
        class:downgrade={isDowngrade}
        class:trial={isOnTrial}
        on:click|preventDefault={handleSubmit}
        disabled={isProcessing || (needsPayment && isLoadingCards)}
      >
        {#if isProcessing}
          <i class="spinner loading icon"></i>
        {/if}
        {getButtonText()}
      </button>
    </div>
  </div>
</Modal>

<style>
  .plan-summary {
    background: linear-gradient(135deg, #f8fafc 0%, #f0f4f8 100%);
    border-radius: 12px;
    padding: 1.25rem;
    margin-bottom: 1rem;
  }

  .plan-summary.trial {
    background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  }

  .plan-summary-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1rem;
  }

  .plan-icon {
    width: 48px;
    height: 48px;
    background: var(--primary);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .plan-icon i {
    color: var(--white);
    font-size: 1.5rem;
    margin: 0;
  }

  .plan-icon.trial {
    background: #16a34a;
  }

  .plan-info {
    flex: 1;
  }

  .plan-name {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--grey);
    margin: 0 0 0.25rem 0;
  }

  .plan-price {
    display: flex;
    align-items: baseline;
    gap: 0.25rem;
  }

  .price-amount {
    font-size: 1.5rem;
    font-weight: 600;
    color: var(--primary);
  }

  .price-period {
    font-size: 0.875rem;
    color: var(--grey-secondary);
  }

  .plan-features-summary {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 1rem;
    padding-left: 0.5rem;
  }

  .feature-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    color: var(--grey);
  }

  .feature-item i {
    color: #45c496;
    font-size: 1rem;
    margin: 0;
    width: 18px;
  }

  .billing-note {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    background: var(--white);
    padding: 0.75rem 1rem;
    border-radius: 8px;
    font-size: 0.8125rem;
    color: var(--grey-secondary);
    line-height: 1.5;
  }

  .billing-note i {
    color: var(--primary);
    margin: 0;
    flex-shrink: 0;
    margin-top: 2px;
  }

  .billing-note.trial {
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
  }

  .billing-note.trial i {
    color: #16a34a;
  }

  .divider {
    height: 1px;
    background: var(--border-color);
    margin: 1.25rem 0;
  }

  .payment-section {
    margin-top: 0.5rem;
  }

  .section-title {
    font-size: 0.9375rem;
    font-weight: 600;
    color: var(--grey);
    margin: 0 0 1rem 0;
  }

  .cards-loading {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 1rem;
    color: var(--grey-secondary);
    font-size: 0.875rem;
  }

  .payment-options {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }

  .payment-option {
    display: flex;
    align-items: center;
    padding: 0.875rem 1rem;
    border: 1px solid var(--border-color);
    border-radius: 8px;
    cursor: pointer;
    transition: border-color 0.2s, background 0.2s;
  }

  .payment-option:hover {
    border-color: var(--primary);
    background: #f8fafc;
  }

  .payment-option.selected {
    border-color: var(--primary);
    background: rgba(5, 108, 242, 0.05);
  }

  .payment-option input[type="radio"] {
    margin-right: 0.75rem;
    accent-color: var(--primary);
  }

  .option-content {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex: 1;
  }

  .card-brand-icon {
    font-size: 1.5rem !important;
    color: var(--grey);
    margin: 0 !important;
  }

  .card-info {
    flex: 1;
    font-size: 0.9375rem;
    color: var(--grey);
  }

  .new-card-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .new-card-form.has-saved-cards {
    padding-top: 0.5rem;
    border-top: 1px solid var(--border-color);
    margin-top: 0.5rem;
  }

  .form-field {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .field-label {
    font-size: 0.8125rem;
    font-weight: 500;
    color: var(--grey);
  }

  .field-input {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    font-size: 1rem;
    font-family: inherit;
    transition: border-color 0.2s, box-shadow 0.2s;
    background: var(--white);
    color: var(--grey);
  }

  .field-input:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(5, 108, 242, 0.1);
  }

  .stripe-input {
    padding: 0.75rem 1rem;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    background: var(--white);
    transition: border-color 0.2s, box-shadow 0.2s;
    min-height: 44px;
  }

  .stripe-input:focus-within {
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(5, 108, 242, 0.1);
  }

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }

  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    padding-top: 0.5rem;
  }

  .cancel-button {
    padding: 0.75rem 1.25rem;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    background: var(--white);
    color: var(--grey);
    font-weight: 500;
    font-size: 0.9375rem;
    cursor: pointer;
    transition: background 0.2s, border-color 0.2s;
  }

  .cancel-button:hover:not(:disabled) {
    background: var(--greyish);
    border-color: var(--grey-secondary);
  }

  .cancel-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .subscribe-button {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1.5rem;
    border: 0;
    border-radius: 6px;
    background: #45c496;
    color: var(--white);
    font-weight: 500;
    font-size: 0.9375rem;
    cursor: pointer;
    transition: background 0.2s;
    box-shadow: 1px 2px 3px rgba(0, 0, 0, 0.2);
  }

  .subscribe-button:hover:not(:disabled) {
    background: #3aaa82;
  }

  .subscribe-button:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .subscribe-button.trial {
    background: var(--primary);
  }

  .subscribe-button.trial:hover:not(:disabled) {
    background: var(--darkprimary);
  }

  .subscribe-button.downgrade {
    background: #d4a106;
  }

  .subscribe-button.downgrade:hover:not(:disabled) {
    background: #b38c05;
  }

  .subscribe-button i {
    margin: 0;
  }

  .plan-summary.downgrade {
    background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
  }

  .plan-icon.downgrade {
    background: #d4a106;
  }

  .billing-note.downgrade {
    background: #fff8e6;
    border: 1px solid #ffc107;
  }

  .billing-note.downgrade i {
    color: #d4a106;
  }

  @media (max-width: 480px) {
    .form-row {
      grid-template-columns: 1fr;
    }

    .plan-summary-header {
      flex-direction: column;
      text-align: center;
    }

    .plan-features-summary {
      padding-left: 0;
    }
  }
</style>
